<template>
  <SidebarTemplate
    :sidebar="sidebar"
    :loading="loading"
    :uuid="uuid"
    class="sidebar-instance-details"
  >
    <template v-if="!loading" slot="header-content">
      <InstanceSlotWrapper :instance="instanceToSlot">
        <template #default="{ renderSlot }">
          <span class="text-gray-500 dark:text-neutral-400">{{
            renderSlot('sidebarHeader', 'type')
          }}</span>
          <span class="text-common">
            {{ renderSlot('sidebarHeader', 'number') }}
          </span>
        </template>
      </InstanceSlotWrapper>
    </template>
    <div v-if="isAdmin" class="mb-1 px-1">
      <div
        class="grid h-9 grid-cols-2 rounded-lg bg-gray-100 dark:bg-neutral-800"
      >
        <button
          class="not-[.active]:hover:bg-gray-200 dark:not-[.active]:hover:bg-neutral-700 cursor-pointer rounded-lg text-sm"
          :class="{ 'active bg-primary text-onPrimary': tab == 'info' }"
          @click.prevent="tab = 'info'"
        >
          {{ $t('titles.Characteristics') }}
        </button>
        <button
          class="not-[.active]:hover:bg-gray-200 dark:not-[.active]:hover:bg-neutral-700 cursor-pointer rounded-lg text-sm"
          :class="{ 'active bg-primary text-onPrimary': tab == 'leads' }"
          @click.prevent="tab = 'leads'"
        >
          {{ $t('titles.Leads') }}
        </button>
      </div>
    </div>
    <template v-if="response">
      <template v-if="tab == 'info'">
        <span
          v-if="
            response.instance?.status?.name &&
            catalogSettings &&
            catalogSettings.show_status
          "
          class="z-3 relative inline-block max-w-[calc(100%-5rem)] truncate rounded-e-lg py-1.5 pe-2.5 ps-2 text-xs leading-[1] text-white"
          :style="{ backgroundColor: response.instance?.status?.color }"
        >
          {{ response.instance?.status?.name }}
        </span>
        <InstanceSlider
          class="mb-3"
          :class="{
            '-mt-5':
              response.instance?.status?.name &&
              catalogSettings &&
              catalogSettings.show_status,
          }"
          :blocks="response.slider"
          :sidebar="sidebar"
        >
          <template #first-slide>
            <div
              class="z-5 pointer-events-none absolute left-0 top-0 h-full w-full"
            >
              <div
                class="flex h-full flex-col items-start justify-end gap-1 pb-3 ps-2"
              >
                <div
                  v-for="(promo, index) in response.promoactions.slice(0, 2)"
                  :key="index"
                  v-hint="{
                    position: 'right-center',
                    yOffset: 8,
                    text: promo.name,
                    showDelay: 600,
                  }"
                  class="size-9 inset-ring-2 inset-ring-black/5 shadow-around pointer-events-auto flex shrink-0 items-center justify-center rounded-lg text-white"
                  :style="{
                    backgroundColor: promo.color,
                    // color: promo.color,
                  }"
                >
                  <lucide-icon :icon="promo.icon" :size="20" />
                </div>
              </div>
            </div>
          </template>
        </InstanceSlider>
        <InstanceSlotWrapper
          :instance="instanceToSlot"
          class="sidebar-instance-details-slots pe-1 ps-2"
        >
          <template #default="{ renderSlot, hasContent }">
            <div
              v-if="
                hasContent('price') ||
                (hasContent('pricePerTotalArea') &&
                  catalogSettings.show_square_meter)
              "
              class="flex flex-wrap items-end gap-x-3 gap-y-1"
            >
              <div
                v-if="hasContent('price')"
                class="text-2xl"
                v-html="renderSlot('instanceDetailsHeader', 'price')"
              ></div>

              <div
                v-if="
                  hasContent('pricePerTotalArea') &&
                  catalogSettings.show_square_meter
                "
                class="text-primary mb-1.5 text-sm"
                v-html="
                  renderSlot('instanceDetailsSubheader', 'pricePerTotalArea')
                "
              ></div>
            </div>

            <div
              v-if="response.estate.type != 2"
              class="*:text-gray-500 dark:*:text-neutral-200 *:text-sm mb-3 mt-1 flex flex-wrap items-center gap-2 px-1"
            >
              <template v-if="hasContent('estate') && estates.length > 1">
                <span>
                  {{ renderSlot('instanceDetailsLocation', 'estate') }}
                </span>
                <span
                  class="size-1 rounded-full bg-gray-400 dark:bg-neutral-400"
                ></span>
              </template>
              <template v-if="hasContent('section')">
                <span>{{
                  renderSlot('instanceDetailsLocation', 'section')
                }}</span>
                <span class="size-1 rounded-full bg-gray-400"></span>
              </template>

              <span v-if="hasContent('storey')">{{
                renderSlot('instanceDetailsLocation', 'storey')
              }}</span>
            </div>

            <div class="mt-2 flex flex-wrap gap-1">
              <span
                v-if="hasContent('rooms')"
                class="dark:inset-ring-neutral-800 inset-ring inset-ring-gray-200 inline-flex items-center rounded-full bg-gray-100 px-2.5 pb-1 pt-1.5 text-sm font-medium leading-3 text-gray-700 dark:bg-neutral-700/50 dark:text-neutral-200"
                v-html="renderSlot('instanceDetailsTag', 'rooms')"
              >
              </span>
              <span
                v-if="hasContent('totalArea')"
                class="dark:inset-ring-neutral-800 inset-ring inset-ring-gray-200 inline-flex items-center rounded-full bg-gray-100 px-2.5 pb-1 pt-1.5 text-sm font-medium leading-3 text-gray-700 dark:bg-neutral-700/50 dark:text-neutral-200"
                v-html="renderSlot('instanceDetailsTag', 'totalArea')"
              >
              </span>
              <span
                v-for="(tag, index) in response.tags"
                :key="`tag-${index}`"
                class="dark:inset-ring-neutral-800 inset-ring inset-ring-gray-200 inline-flex items-center rounded-full bg-gray-100 px-2.5 pb-1 pt-1.5 text-sm font-semibold leading-3 text-gray-700 dark:bg-neutral-700/50 dark:text-neutral-200"
              >
                <!-- <div
                  class="size-1.5 rounded-xs me-1"
                  :style="{ backgroundColor: tag.color }"
                ></div> -->
                <span class="">
                  {{ tag.name }}
                </span>
              </span>
            </div>
          </template>
        </InstanceSlotWrapper>
        <div
          v-if="
            firstToken == 'guliver' &&
            instanceToSlot.price &&
            instanceToSlot.status?.type === 1 &&
            parkingPrice
          "
          class="mb-6 mt-3 pe-1 ps-2 text-sm"
        >
          <div
            v-if="instanceToSlot.price && parkingPrice"
            class="mb-1 flex items-center justify-between"
          >
            <span>Стоимость без паркинга:</span>
            <span class="whitespace-nowrap">
              {{
                $formatCurrency(
                  instanceToSlot.price,
                  currentCurrency,
                  $i18n.locale
                )
              }}
            </span>
          </div>
          <div
            v-if="parkingPrice"
            class="mb-1 flex items-center justify-between"
          >
            <span> Стоимость паркинга: </span>
            <span class="whitespace-nowrap">
              {{ $formatCurrency(parkingPrice, currentCurrency, $i18n.locale) }}
            </span>
          </div>
          <div
            v-if="parkingPrice && instanceToSlot.price"
            class="mb-1 flex items-center justify-between"
          >
            <span> Стоимость квартиры с паркингом: </span>
            <span class="whitespace-nowrap">
              {{
                $formatCurrency(
                  instanceToSlot.price + parkingPrice,
                  currentCurrency,
                  $i18n.locale
                )
              }}
            </span>
          </div>
          <!-- <div v-if="instance.layout?.area_total">
            Стоимость м<sup>2</sup>:

            <span class="fw-700">{{
              $n(
                instance.price / instance.layout.area_total,
                'currency',
                currency
              )
            }}</span>
          </div> -->
        </div>

        <div class="mt-3 flex justify-end gap-2 pe-1 ps-2">
          <template v-if="isAdmin">
            <button
              v-if="!isInstanceLinkedToCrm"
              class="btn btn-primary h-10 w-full justify-center"
              @click="togglelinkWithCrm()"
            >
              <LucideIcon class="me-1" icon="Pin" :size="16" />
              {{ $t('control.Link to CRM') }}
            </button>
            <button
              v-else
              class="btn h-10 w-full justify-center bg-red-400 text-white transition-colors hover:bg-red-500"
              @click="togglelinkWithCrm(true)"
            >
              <LucideIcon class="me-1" icon="PinOff" :size="16" />
              {{ $t('control.Unlink from CRM') }}
            </button>
          </template>
          <template v-else>
            <button
              v-if="
                response.instance.status.type == 1 &&
                catalogSettings?.allow_requests
              "
              class="btn btn-primary h-10 w-full justify-center"
              @click.prevent="
                OPEN_MODAL({
                  type: 'order',
                  payload: {
                    instance: instanceToSlot,
                  },
                })
              "
            >
              {{ $t('control.Order') }}
            </button>
            <button
              v-else
              class="btn btn-primary h-10 w-full justify-center"
              @click.prevent="findSimilar()"
            >
              {{ $t('control.Find similar') }}
            </button>
          </template>

          <!-- <button class="btn btn-primary w-full justify-center">sd</button> -->
          <!-- <button class="btn btn-primary w-full justify-center">sd</button> -->

          <button
            v-if="!hideFav"
            class="size-10 text-primary group flex shrink-0 cursor-pointer items-center justify-center rounded-lg bg-gray-100 transition-colors hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700"
            :class="{
              ' active rounded-lg text-red-500 ': isLiked(response.instance.id),
            }"
            @click.stop.prevent="TOGGLE_LIKE(response.instance.id)"
          >
            <LucideIcon
              class="transition-[fill] group-[.active]:fill-red-500"
              icon="RPTHeart"
              :size="20"
            />
          </button>
        </div>
        <div class="mt-2 flex justify-end gap-2 pe-1 ps-2">
          <a
            target="_blank"
            :href="pdfLink"
            class="btn ring-primary hover:bg-primary hover:text-onPrimary text-primary h-10 w-full cursor-pointer justify-center ring-1 transition-colors"
          >
            <LucideIcon class="me-1" icon="PdfFile" :size="16" />
            {{ $t('control.Show PDF') }}
          </a>
          <button
            v-if="response.layout.video_link"
            v-hint="{
              enabled: response.layout.tour_link && response.layout.video_link,
              position: 'bottom',
              yOffset: 8,
              text: $t('control.Video'),
              showDelay: 600,
            }"
            class="btn min-w-10 h-10 justify-center gap-1 bg-red-500 text-white hover:bg-red-600"
            :class="{
              'w-full': !(
                response.layout.tour_link && response.layout.video_link
              ),
            }"
            @click.prevent="
              OPEN_SIDEBAR_MODAL({
                type: 'video',
                payload: {
                  src: response.layout.video_link,
                },
                sidebar_id: sidebar.id,
              })
            "
          >
            <lucide-icon icon="Youtube" :size="22" />
            <span
              v-if="!(response.layout.tour_link && response.layout.video_link)"
            >
              {{ $t('control.Video') }}
            </span>
          </button>
          <button
            v-if="response.layout.tour_link"
            v-hint="{
              enabled: response.layout.tour_link && response.layout.video_link,
              position: 'bottom',
              yOffset: 8,
              text: $t('control.Tour'),
              showDelay: 600,
            }"
            class="btn min-w-10 ring-primary hover:bg-primary hover:text-onPrimary text-primary h-10 h-10 cursor-pointer justify-center gap-1 ring-1 transition-colors"
            :class="{
              'w-full': !(
                response.layout.tour_link && response.layout.video_link
              ),
            }"
            @click.prevent="
              OPEN_SIDEBAR_MODAL({
                type: 'iframe',
                payload: {
                  src: response.layout.tour_link,
                },
                sidebar_id: sidebar.id,
              })
            "
          >
            <lucide-icon icon="Rotate3d" :size="18" />
            <span
              v-if="!(response.layout.tour_link && response.layout.video_link)"
            >
              {{ $t('control.Tour') }}
            </span>
          </button>
          <Dropdown
            :fullscreen="!$windowSize.isDesktop"
            :chevron-size="0"
            button-content-class="flex justify-center w-full"
            button-class="size-10 btn btn-primary shrink-0 "
          >
            >
            <template #label-icon>
              <LucideIcon class="" icon="Share2" :size="18" />
            </template>
            <template #label> </template>
            <template #title> {{ $t('titles.Share link') }}</template>
            <template #content="{ closeDropdown }">
              <button
                class="hover:text-primary mb-0.5 flex w-full cursor-pointer items-center rounded-lg px-2 py-2 text-sm hover:bg-gray-100"
                @click="
                  share('telegram');
                  closeDropdown();
                "
              >
                <lucide-icon
                  class="text-telegram me-2"
                  icon="Telegram"
                  :size="22"
                />
                <span>Telegram</span>
              </button>
              <button
                class="hover:text-primary mb-0.5 flex w-full cursor-pointer items-center rounded-lg px-2 py-2 text-sm hover:bg-gray-100"
                @click="
                  share('whatsapp');
                  closeDropdown();
                "
              >
                <lucide-icon
                  class="text-whatsapp me-2"
                  icon="Whatsapp"
                  :size="22"
                />
                <span>WhatsApp</span>
              </button>

              <button
                class="hover:text-primary mb-0.5 flex w-full cursor-pointer items-center rounded-lg px-2 py-2 text-sm hover:bg-gray-100"
                @click="
                  share('viber');
                  closeDropdown();
                "
              >
                <lucide-icon class="text-viber me-2" icon="Viber" :size="22" />
                <span>Viber</span>
              </button>
              <div
                class="form-control group flex h-10 cursor-pointer items-center hover:bg-gray-100"
                @click="copyUrl"
              >
                <input
                  ref="shareUrl"
                  type="text"
                  :value="url"
                  class="pointer-events-none w-full text-xs"
                  readonly
                />
                <lucide-icon
                  class="group-hover:text-primary ms-2 shrink-0 text-gray-400 transition-colors dark:text-neutral-600"
                  :icon="urlCopied ? 'Check' : 'ClipboardCopy'"
                  :size="16"
                />
              </div>
            </template>
          </Dropdown>
        </div>

        <div class="mt-2 grid grid-cols-2 gap-1 pe-1 ps-2">
          <button
            v-for="button in customButtons"
            :key="button.id"
            class="btn flex h-10 justify-center rounded-lg px-2"
            :class="[
              button.width == 2 ? 'col-span-2' : '',
              buttonClass[button.payload.variant],
            ]"
            @click="processButton(button.value, button.payload.isIframe)"
          >
            <lucide-icon class="me-2" :icon="button.payload.icon" size="16" />
            {{ button.translated_name }}
          </button>
        </div>

        <div class="mt-8 pe-1 ps-2">
          <template v-if="characteristicsFields.length">
            <div class="mb-4 text-lg">{{ $t('titles.Characteristics') }}:</div>
            <InstanceFields :fields="characteristicsFields" />
          </template>
        </div>
        <div
          class="custom-scroll-x mt-5 flex snap-x snap-mandatory gap-2 overflow-x-auto px-2"
        >
          <div
            v-for="promo in response.promoactions"
            :key="promo.id"
            class="border-1 relative w-full shrink-0 snap-start scroll-mx-2 rounded-lg border-gray-300 p-1 dark:border-neutral-700"
            :class="{
              'max-w-[calc(100%-4rem)]': response.promoactions.length > 1,
            }"
          >
            <div class="mb-2 flex items-center gap-1 leading-[1]">
              <div
                class="size-8 flex shrink-0 items-center justify-center rounded-lg text-white"
                :style="{
                  backgroundColor: promo.color,
                }"
              >
                <lucide-icon :icon="promo.icon" :size="18" />
              </div>
              {{ promo.name }}
            </div>
            <div class="px-1">
              <p
                class="line-clamp-3 text-sm text-gray-600 dark:text-neutral-300"
              >
                {{ promo.description }}
              </p>
              <a
                v-if="promo.link"
                target="_blank"
                :href="promo.link"
                class="text-primary mt-2 inline-flex cursor-pointer items-center text-sm"
              >
                <lucide-icon
                  icon="ExternalLink"
                  :size="14"
                  class="me-1 shrink-0"
                />
                {{ promo.button }}
              </a>
            </div>
          </div>
        </div>

        <div class="mt-5 pe-1 ps-2">
          <template v-if="additionalCharacteristicsFields.length">
            <div class="mb-4 mt-4 text-lg">
              {{ $t('titles.Additional characteristics') }}:
            </div>
            <InstanceFields :fields="additionalCharacteristicsFields" />
          </template>

          <template v-if="estateFields.length">
            <div class="mb-3 mt-5 text-lg">
              <template v-if="catalogSettings?.project_id === 101">
                {{ $t('titles.About project') }}:
              </template>
              <template v-else> {{ $t('titles.About estate') }}: </template>
            </div>

            <p
              v-if="projectDescription"
              class="mb-4 text-sm font-normal text-gray-700 dark:text-neutral-300"
              v-html="projectDescription"
            ></p>
            <InstanceFields :fields="estateFields" />
          </template>
          <template v-if="additionalEstateFields.length">
            <div class="mb-4 mt-6 text-lg">
              {{ $t('titles.Additional characteristics') }}:
            </div>
            <InstanceFields :fields="additionalEstateFields" />
          </template>
        </div>

        <ConstructionProgress
          v-if="response.constructionProgresses.length"
          :progress="response.constructionProgresses"
          :sidebar="sidebar"
          class="mt-5"
        />

        <div v-if="similarInstances.length" class="pe-1 ps-2">
          <div class="mb-4 mt-5 text-lg">{{ $t('titles.Similar') }}:</div>
          <InstanceCard
            v-for="similarItem in similarInstances"
            :key="similarItem.instance.id"
            view="list"
            :minified="true"
            :instance="similarItem.instance"
            class="mb-2"
          />
        </div>
      </template>
      <template v-if="tab == 'leads' && isAdmin">
        <div v-if="leadsLoading" class="flex justify-center py-4">
          <lucide-icon
            icon="Loading"
            class="animate-spin"
            size="18"
          ></lucide-icon>
        </div>
        <template v-else>
          <div v-if="leads.length" class="mb-3 pe-1 ps-2">
            <div
              v-for="(lead, index) in leads"
              :key="index"
              class="border-1 rounded border-gray-100 p-3 dark:border-neutral-800"
            >
              <div class="mb-2 line-clamp-2 text-sm">
                {{ lead.name }}
              </div>
              <div
                class="mb-1 line-clamp-2 text-xs text-gray-400 dark:text-neutral-400"
              >
                {{ lead?.responsible_user?.name }}
              </div>
              <a
                class="text-primary hover:text-primary-active flex items-center text-xs"
                :href="lead.link"
                target="_blank"
              >
                <lucide-icon
                  icon="ExternalLink"
                  class="me-1"
                  size="14"
                ></lucide-icon>
                {{ $t('control.View') }}
              </a>
            </div>
          </div>
          <div v-else class="text-center">
            <h4 class="mt-4">
              {{ $t('titles.No items found') }}
            </h4>
          </div>
        </template>
      </template>
    </template>
  </SidebarTemplate>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

import SidebarTemplate from '@/components/sidebar/SidebarTemplate.vue';
import InstanceSlider from '../components/InstanceSlider.vue';
import InstanceFields from '../components/InstanceFields.vue';
import Dropdown from '@/components/ui/Dropdown';

import ConstructionProgress from '@/components/sidebar/templates/components/ConstructionProgress';
import Hint from '@/libs/directives/Hint';

import InstanceSlotWrapper from '@/components/slots/InstanceSlotWrapper';
import InstanceCard from '@/components/cards/InstanceCard';

export default {
  name: 'InstanceDetails',
  directives: {
    Hint,
  },
  components: {
    InstanceCard,
    InstanceSlotWrapper,
    InstanceFields,
    ConstructionProgress,
    Dropdown,
    InstanceSlider,
    SidebarTemplate,
  },
  props: {
    sidebar: {
      type: Object,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      response: null,
      urlCopied: false,

      url: window.location.href,
      leads: [],
      leadsLoading: true,
      tab: 'info',
      loading: true,
      abortController: null,
      similarInstances: [],
    };
  },
  computed: {
    ...mapGetters([
      'isLiked',
      'crm',
      'estates',
      'catalogSettings',
      'firstToken',
      'secondToken',
      'project',
      'isAdmin',
      'currentLanguage',
      'currentCurrency',
      'hideFav',
    ]),

    pdfLink() {
      return (
        `/${this.currentLanguage?.name}/${this.firstToken}` +
        (this.secondToken ? `/${this.secondToken}` : '') +
        `/pdf/${this.response.instance.id}`
      );
    },

    buttonClass() {
      return {
        primary: 'bg-primary text-onPrimary hover:bg-primary-active',
        secondary: 'bg-gray-100 text-neutral-700 hover:bg-gray-200',
        success: 'bg-green-500 text-white hover:bg-green-600',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        telegram: 'bg-telegram text-white',
        viber: 'bg-viber text-white',
        whatsapp: 'bg-whatsapp text-white',
        'outline-primary':
          'inset-ring-1 inset-ring-primary text-primary hover:bg-primary hover:text-onPrimary',
        'outline-secondary':
          'inset-ring-1 inset-ring-gray-300 text-neutral-700 hover:bg-gray-200 ',
        'outline-success':
          'inset-ring-1 inset-ring-green-500 text-green-500 hover:bg-green-500  hover:text-white',
        'outline-danger':
          'inset-ring-1 inset-ring-red-500 text-red-500 hover:bg-red-500  hover:text-white',
      };
    },

    instanceToSlot() {
      if (!this.response) {
        return {};
      }
      return {
        id: this.response.instance.id,
        number: this.response.instance.number,
        price: this.response.instance.price,
        extra_price: this.parkingPrice,
        old_price: this.response.instance.old_price,
        status: this.response.instance.status,
        estate: this.response.estate,
        section: this.response.section,
        storey: this.response.storey,
        layout: this.response.layout,
        tags: this.response.tags,
        promoactions: this.response.promoactions.map((promo) => promo.id),
      };
    },

    parkingPrice() {
      if (!this.response?.fields) {
        return 0;
      }
      const parkingField = this.response.fields.find(
        (field) => field.name === 'Parking price'
      );

      return parkingField ? Number(parkingField.value) : 0;
    },

    projectDescription() {
      return this.response.fields.find(
        (field) => field.name === 'Description' && field.model == 'project'
      ).value;
    },

    customButtons() {
      return this.response.fields
        .filter(
          (item) =>
            item.value != null &&
            (this.isAdmin || item.public) &&
            item.type == 6
        )
        .map((item, index, array) => ({
          ...item,
          width: index === array.length - 1 && index % 2 === 0 ? 2 : 1,
        }));
    },

    filteredFields() {
      if (!this.response) {
        return [];
      }
      // const TEXT = 1;
      // const TEXTWITHIF = 2;
      // const SELECT = 3;
      // const SELECTWITHIF = 4;
      // const PHOTOS = 5;
      // const BUTTON = 6;
      // const QUILL = 7;
      // const BOOLSELECT = 8;
      // const NUMBER = 9;
      // const COLOR = 10;

      let allowedTypes = [1, 2, 3, 4, 8, 9];
      return this.response.fields.filter(
        (item) =>
          item.value != null &&
          (this.isAdmin || item.public) &&
          allowedTypes.includes(item.type)
      );
    },
    characteristicsFields() {
      if (!this.response) {
        return [];
      }
      let ignoreFields = ['Type', 'Rooms'];
      if (this.response.layout.type != 'living') {
        ignoreFields = [
          ...ignoreFields,
          ...['Living area', 'Kitchen area', 'Renovation'],
        ];
      }
      let icons = {
        'Living area': 'Living',
        'Kitchen area': 'Kitchen',
        Renovation: 'Renovation',
        'Balcony count': 'Balcony',
        'Window view': 'WindowView',
        'Bathroom count': 'Bathroom',
      };

      return this.setFieldsFormat(
        this.filteredFields
          .filter(
            (item) =>
              !ignoreFields.includes(item.name) &&
              item.model == 'layout' &&
              item.default == true
          )
          .map((item) => {
            if (icons[item.name]) {
              item.icon = icons[item.name];
            }
            return item;
          })
      );
    },
    additionalCharacteristicsFields() {
      if (!this.response) {
        return [];
      }
      return this.setFieldsFormat(
        this.filteredFields.filter(
          (item) =>
            (item.model == 'layout' || item.model == 'instance') &&
            item.default == false
        )
      );
    },

    isInstanceLinkedToCrm() {
      return this.leads.some((lead) => lead.id == this.crm.id);
    },

    estateFields() {
      if (!this.response) {
        return [];
      }
      let ignoreFields = ['Description', 'Address', 'Currency', 'Area unit'];

      const fields = this.filteredFields.filter(
        (item) =>
          !ignoreFields.includes(item.name) &&
          (item.model == 'estate' || item.model == 'project') &&
          item.default == true
      );

      // Handle construction end fields
      const yearField = fields.find(
        (field) => field.name === 'Construction end year'
      );
      const quarterField = fields.find(
        (field) => field.name === 'Construction end quater'
      );

      if (yearField && quarterField) {
        const filteredFields = fields.filter(
          (field) =>
            !['Construction end year', 'Construction end quater'].includes(
              field.name
            )
        );

        filteredFields.push({
          value: this.$t('titles.Construction end', {
            year: yearField.value,
            quater: quarterField.value,
          }),
          name: 'Construction end',
          area: false,
          translatable: false,
          format: 'text',
        });

        return this.setFieldsFormat(filteredFields);
      }

      return this.setFieldsFormat(fields);
    },
    additionalEstateFields() {
      if (!this.response) {
        return [];
      }

      return this.setFieldsFormat(
        this.filteredFields.filter(
          (item) =>
            (item.model == 'estate' || item.model == 'project') &&
            item.default == false
        )
      );
    },
  },
  watch: {
    uuid: {
      async handler() {
        if (this.abortController) {
          this.abortController.abort(); // Отмена предыдущего запроса
        }

        this.abortController = new AbortController();

        await this.handleInstanceId(
          this.sidebar.subcontext,
          this.abortController.signal
        );
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions([
      'OPEN_SIDEBAR_MODAL',
      'TOGGLE_LIKE',
      'OPEN_MODAL',
      'GET_SIMILAR',
      'axiosWrapped',
      'LOAD_START',
      'LOAD_END',
    ]),
    async togglelinkWithCrm(unlink = false) {
      let data = {
        instance_id: this.response.instance.id,
        crm_lead_id: this.crm.id,
        crm_type: this.crm.type,
        crm_domain: this.crm.domain,
        crm_lead_type: this.crm.lead_type,
        crm_status_id: this.crm.status_id,
        crm_category_id: this.crm.category_id,
        method: 'post',
      };
      const url = `public/lead/${unlink ? 'unlink' : 'link'}`;
      this.LOAD_START('crmLink');
      try {
        await this.axiosWrapped({
          url: url,
          body: data,
          method: 'post',
        });

        if (this.$metrika) {
          this.$metrika.reachGoal('RPTcrm');
        }
        if (this.$gtm) {
          this.$gtm.trackEvent({ event: 'RPTcrm' });
        }
        this.LOAD_END('crmLink');

        this.OPEN_MODAL({
          type: 'crmSuccessLink',
        });
        let message = 'addInstanceRPTWidget';

        if (unlink == true) {
          message = 'removeInstanceRPTWidget';
        }
        window.parent.postMessage(
          {
            type: message,
          },
          '*'
        );
        if (this.openedFromUrl) {
          window.parent.postMessage(
            {
              type: message,
            },
            this.openedFromUrl
          );
        }
      } catch (e) {
        this.LOAD_END('crmLink');

        this.OPEN_MODAL({
          type: 'errorModal',
        });
        console.log(e);
      }
    },
    async handleInstanceId(id, signal) {
      this.url = window.location.href;
      this.tab = 'info';
      this.loading = true;
      try {
        const response = await this.axiosWrapped({
          url: `public/instances/${id}`,
          signal,
        });
        this.loading = false;
        this.response = response.data.data;

        this.similarInstances = await this.GET_SIMILAR({
          instance: this.instanceToSlot,
        });
        if (window.RPTAnalytics) {
          window.RPTAnalytics.event('showInstance', { id: id * 1 });
        }
        this.$posthog.capture('showInstance', {
          id: id * 1,
        });

        // TODO:

        if (id != null && this.isAdmin && this.crm) {
          this.leads = [];
          this.leadsLoading = true;
          let url = `public/crm-leads/${this.crm.type}/${id}`;
          if (this.crm.type == 'RptCrm') {
            url = `public/crm-leads/${this.crm.type}/${this.crm.crm_id}/instance/${id}`;
          }
          try {
            const leadsResponse = await this.axiosWrapped({
              url,
              signal,
            });
            this.leads = leadsResponse.data;
          } catch (e) {
            if (e.name !== 'CanceledError') {
              console.error(e);
            }
          }
          this.leadsLoading = false;
        }
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error(error);
          this.loading = false;
        }
      }
    },

    setFieldsFormat(fields) {
      let number = [
        'Balcony count',
        'Bathroom count',
        'Construction end year',
        'Construction end quater',
      ];
      let area = ['Living area', 'Kitchen area'];
      return fields.map((item) => {
        item.format = 'text';
        if (number.includes(item.name)) {
          item.value = parseFloat(item.value.replace(/,/, '.'));

          item.format = 'number';
        } else if (area.includes(item.name)) {
          item.value = parseFloat(item.value.replace(/,/, '.'));
          item.format = 'area';
        } else if (item.default == true) {
          item.format = 'translate';
        }
        return item;
      });
    },

    processButton(url, isIframe) {
      if (isIframe) {
        this.OPEN_SIDEBAR_MODAL({
          type: 'iframe',
          payload: {
            src: url,
          },
          sidebar_id: this.sidebar.id,
        });
        return;
      }
      window.open(url, '_blank');
    },

    findSimilar() {
      const query = {
        type: this.instanceToSlot.layout?.type,
        min_area: this.instanceToSlot.layout?.area_total - 20,
        max_area: this.instanceToSlot.layout?.area_total + 20,
      };
      if (this.instanceToSlot.layout?.type == 'living') {
        query.rooms = JSON.stringify([this.instanceToSlot.layout?.label]);
      }
      Object.keys(query).forEach(
        (key) => query[key] === undefined && delete query[key]
      );
      this.routerPush({
        name: 'Premises',
        query: query,
      });
    },

    async copyUrl() {
      const element = this.$refs.shareUrl;
      element.select();
      element.setSelectionRange(0, 99999);
      this.urlCopied = true;

      try {
        await navigator.clipboard.writeText(element.value);
      } catch (err) {
        document.execCommand('copy');
        console.error('Failed to copy using Clipboard API:', err);
      }

      setTimeout(() => {
        this.urlCopied = false;
      }, 1000);
    },

    share(social) {
      let url = window.location.href.split('?')[0];
      url = url.replace('#', '');
      url = encodeURIComponent(url);
      const text = encodeURI(this.title);
      let link = '';
      switch (social) {
        case 'telegram':
          link = 'https://t.me/share/url?url=' + url + '&text=' + text;
          break;
        case 'whatsapp':
          link = 'https://wa.me/?text=' + text + encodeURI(' : ') + url;
          break;
        case 'viber':
          link = 'viber://forward?text=' + text + encodeURI(' : ') + url;
          break;
      }
      window.open(link, '_blank');
    },
  },
};
</script>
<style></style>
